/**
 * ProductTable component
 * Displays production metrics grouped by product SKU
 * Shows raw material names and allocated weight inline per product
 * With dark mode support
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { formatYield, getYieldColor } from '../../../../hooks/useYieldFactor';
import { ProductSummary } from '../../../../services/productionService';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface ProductTableProps {
  products: ProductSummary[];
}

type SortField = 'product_name' | 'sessions_count' | 'boxes_packed' | 'total_weight' | 'yield_percentage';
type SortDirection = 'asc' | 'desc';

const ProductTable: React.FC<ProductTableProps> = ({ products }) => {
  const { t } = useLanguage();
  const [sortField, setSortField] = useState<SortField>('total_weight');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('endOfDaySummary.noData')}
      </div>
    );
  }

  // Check if any product has raw materials data
  const hasRawMaterials = products.some(p => p.raw_materials && p.raw_materials.length > 0);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              {t('endOfDaySummary.productItemNumber')}
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('product_name')}
            >
              {t('endOfDaySummary.productName')}
              <SortIcon field="product_name" />
            </th>
            {hasRawMaterials && (
              <>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('endOfDaySummary.rawMaterial')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('endOfDaySummary.allocated')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('endOfDaySummary.used')}
                </th>
              </>
            )}
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('sessions_count')}
            >
              {t('endOfDaySummary.sessionsCount')}
              <SortIcon field="sessions_count" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('boxes_packed')}
            >
              {t('endOfDaySummary.boxesPacked')}
              <SortIcon field="boxes_packed" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('total_weight')}
            >
              {t('endOfDaySummary.weight')}
              <SortIcon field="total_weight" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('yield_percentage')}
            >
              {t('endOfDaySummary.yield')}
              <SortIcon field="yield_percentage" />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedProducts.map((product, index) => {
            const materials = product.raw_materials || [];
            const rowBg = index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/30';

            return (
              <tr key={product.product_sku} className={rowBg}>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {product.product_sku}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {product.product_name || '-'}
                  {product.product_chinese_name && (
                    <span className="text-gray-500 dark:text-gray-400"> ({product.product_chinese_name})</span>
                  )}
                </td>
                {hasRawMaterials && (
                  <>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {materials.length > 0 ? (
                        <div className="space-y-0.5">
                          {materials.map((mat, mi) => (
                            <div key={mi} className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {mat.item_number} - {mat.item_name || '?'}{mat.item_chinese_name ? ` (${mat.item_chinese_name})` : ''}
                            </div>
                          ))}
                          {materials.length > 1 && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 pt-0.5 border-t border-gray-200 dark:border-gray-600">Total</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm align-top">
                      {materials.length > 0 ? (
                        <div className="space-y-0.5">
                          {materials.map((mat, mi) => (
                            <div key={mi} className="text-xs text-gray-900 dark:text-gray-100 whitespace-nowrap">
                              {mat.allocated_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
                            </div>
                          ))}
                          {materials.length > 1 && (
                            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap pt-0.5 border-t border-gray-200 dark:border-gray-600">
                              {(product.initial_material_weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm align-top">
                      {materials.length > 0 ? (
                        <div className="space-y-0.5">
                          {materials.map((mat, mi) => (
                            <div key={mi} className="text-xs whitespace-nowrap">
                              {mat.used_weight != null ? (
                                <span className="text-gray-900 dark:text-gray-100">
                                  {mat.used_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </div>
                          ))}
                          {materials.length > 1 && (
                            <div className="text-xs font-semibold whitespace-nowrap pt-0.5 border-t border-gray-200 dark:border-gray-600">
                              {materials.some(m => m.used_weight != null) ? (
                                <span className="text-gray-900 dark:text-gray-100">
                                  {materials.filter(m => m.used_weight != null).reduce((sum, m) => sum + (m.used_weight || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </>
                )}
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                  {product.sessions_count}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                  {product.boxes_packed.toLocaleString()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                  {product.total_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-medium ${getYieldColor(product.yield_percentage)}`}>
                  {formatYield(product.yield_percentage)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
